import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, errMsg } from "@/lib/format";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { Wallet, CreditCard as CCIcon, AlertTriangle, Sparkles } from "lucide-react";
import InfoTip from "@/components/InfoTip";
import AssistantPanel from "@/components/AssistantPanel";
import HouseholdKPICard from "@/components/HouseholdKPICard";
import { useAuth } from "@/context/AuthContext";

const CUR_SYM = { DOP: "RD$", USD: "US$", EUR: "€", USDT: "₮" };
const SYM = (c) => CUR_SYM[(c || "DOP").toUpperCase()] || `${c} `;

const DONUT_COLORS = ["#8B5CF6", "#C084FC", "#38BDF8", "#F43F5E", "#F59E0B", "#10B981", "#EC4899", "#3B82F6", "#22D3EE", "#A78BFA"];

export default function AnalyticsPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/summary").then(r => setStats(r.data)).catch(e => toast.error(errMsg(e)));
  }, []);

  const totalCat = (stats?.by_category || []).reduce((s, c) => s + c.amount, 0);
  const nw = stats?.net_worth;
  const baseCur = stats?.base_currency || "DOP";

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Análisis</div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-1">Tus gastos en detalle</h1>
      </div>

      {/* Patrimonio neto — power users */}
      {nw && (
        <div className="card-surface p-5 sm:p-6" data-testid="networth-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold flex items-center gap-2">
              Patrimonio neto
              <InfoTip
                testid="info-patrimonio-analytics"
                text="Es tu dinero real: todo lo que tienes en cuentas, menos lo que debes en tarjetas y préstamos."
              />
            </h2>
            <div className="text-xl sm:text-2xl font-bold tabular-nums text-white" data-testid="networth-total">
              {SYM(baseCur)}{Number(nw.net_worth_base || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <NWRow icon={Wallet} label={`Cuentas (${nw.accounts_count || 0})`} amount={nw.accounts_total_base} sign="+" baseCur={baseCur} tone="emerald" testid="nw-accounts"/>
            <NWRow icon={CCIcon} label={`Tarjetas (${nw.credit_cards_count || 0})`} amount={nw.cards_debt_base} sign="−" baseCur={baseCur} tone="rose" testid="nw-cards"/>
            <NWRow icon={AlertTriangle} label={`Deudas (${nw.active_debts_count || 0})`} amount={nw.debts_total_base} sign="−" baseCur={baseCur} tone="rose" testid="nw-debts"/>
          </div>
        </div>
      )}

      {/* Asistente IA + Hogar KPIs */}
      <AssistantPanel />
      <HouseholdKPICard effectiveTier={user?.effective_tier} />

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Donut */}
        <div className="card-surface p-6" data-testid="donut-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Gastos por categoría</h2>
            <span className="text-xs text-slate-500">Este mes</span>
          </div>
          <div className="text-2xl font-bold text-white">{fmtMoney(totalCat)}</div>

          <div className="h-[260px] mt-4">
            {(!stats?.by_category || stats.by_category.length === 0) ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">Sin datos este mes</div>
            ) : (
              <ChartReady height={260}>
                {({ width, height }) => (
                  <PieChart width={width} height={height}>
                    <Pie data={stats.by_category} dataKey="amount" nameKey="category" innerRadius={70} outerRadius={105} paddingAngle={2}>
                      {stats.by_category.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} stroke="#0b0a10" strokeWidth={2}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#15141B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "white" }} formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                )}
              </ChartReady>
            )}
          </div>
          <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
            {(stats?.by_category || []).map((c, i) => {
              const pct = totalCat ? (c.amount / totalCat) * 100 : 0;
              return (
                <div key={c.category} className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                  <span className="text-sm text-white flex-1">{c.category}</span>
                  <span className="text-xs text-slate-500 w-12 text-right">{pct.toFixed(0)}%</span>
                  <span className="text-sm font-semibold w-28 text-right">{fmtMoney(c.amount)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bars 6m */}
        <div className="card-surface p-6" data-testid="bars-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold">Últimos 6 meses</h2>
            <span className="text-xs text-slate-500">Ingresos / Gastos</span>
          </div>
          <div className="h-[330px] mt-4">
            <ChartReady height={330}>
              {({ width, height }) => (
                <BarChart data={stats?.bars || []} width={width} height={height}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false}/>
                  <XAxis dataKey="month" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false}/>
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}/>
                  <Tooltip cursor={{ fill: "rgba(139,92,246,0.06)" }} contentStyle={{ background: "#15141B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, color: "white" }} formatter={(v) => fmtMoney(v)}/>
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }}/>
                  <Bar dataKey="income" name="Ingresos" fill="#8B5CF6" radius={[6,6,0,0]}/>
                  <Bar dataKey="expense" name="Gastos" fill="#F43F5E" radius={[6,6,0,0]}/>
                </BarChart>
              )}
            </ChartReady>
          </div>
        </div>
      </div>
    </div>
  );
}

function NWRow({ icon: Icon, label, amount, sign, baseCur, tone, testid }) {
  const sym = CUR_SYM[baseCur] || baseCur;
  const tones = {
    emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-200",
    rose: "bg-rose-500/10 border-rose-500/20 text-rose-200",
  };
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${tones[tone] || tones.emerald}`} data-testid={testid}>
      <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" strokeWidth={2}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold truncate">{label}</div>
        <div className="text-sm font-bold tabular-nums text-white truncate">
          <span className="opacity-70 mr-0.5">{sign}</span>{sym}{Math.abs(Number(amount || 0)).toLocaleString("es-DO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </div>
      </div>
    </div>
  );
}

/**
 * Mounts a recharts chart only after the parent has a measured width > 0,
 * eliminating the `width(-1) height(-1)` warning that ResponsiveContainer
 * emits on its first render.
 */
function ChartReady({ children, height }) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(([entry]) => {
      const next = Math.floor(entry.contentRect.width);
      if (next > 0) setW(next);
    });
    ro.observe(el);
    const initial = Math.floor(el.getBoundingClientRect().width);
    if (initial > 0) setW(initial);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: "100%", height }}>
      {w > 0 ? children({ width: w, height }) : null}
    </div>
  );
}

